import {
  Directive,
  DoCheck,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  Optional,
  Renderer2
} from '@angular/core';
import { NgModel } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LinkFormattingService } from '../services/link-formatting.service';
import { SafeHtmlService } from '../services/safe-html.service';

/**
 * Verifies HTTPS links in plain-text textareas.
 * Formatted link display is shown only in readonly/disabled mode.
 */
@Directive({
  selector: 'textarea[pInputTextarea]'
})
export class UserGeneratedTextEditorDirective implements DoCheck, OnDestroy {
  @Input() noLinkFormat = false;

  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private displayElement: HTMLElement | null = null;
  private lastRenderedValue: string | null = null;
  private wasReadOnly = false;
  private valueChangesSub: Subscription | null = null;
  private renderVersion = 0;

  constructor(
    private elementRef: ElementRef<HTMLTextAreaElement>,
    private renderer: Renderer2,
    @Optional() private ngModel: NgModel,
    private linkFormattingService: LinkFormattingService,
    private safeHtmlService: SafeHtmlService
  ) {
    if (this.ngModel) {
      this.valueChangesSub = this.ngModel.valueChanges?.subscribe(() => {
        if (this.isReadOnly()) {
          this.lastRenderedValue = null;
          void this.showReadOnlyDisplay();
        }
      });
    }
  }

  ngDoCheck(): void {
    if (this.noLinkFormat) {
      return;
    }

    const isReadOnly = this.isReadOnly();
    if (isReadOnly) {
      void this.showReadOnlyDisplay();
    } else if (this.wasReadOnly) {
      this.hideReadOnlyDisplay();
    }
    this.wasReadOnly = isReadOnly;
  }

  ngOnDestroy(): void {
    this.renderVersion++;
    this.valueChangesSub?.unsubscribe();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.hideReadOnlyDisplay();
  }

  @HostListener('input')
  onInput(): void {
    if (this.noLinkFormat || !this.ngModel || this.isReadOnly()) {
      return;
    }

    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    this.debounceTimer = setTimeout(async () => {
      const current = this.ngModel?.model;
      if (!current || typeof current !== 'string') {
        return;
      }

      const result = await this.linkFormattingService.processNewUrls(current, false);
      if (result.modified && result.texte !== current) {
        this.ngModel.control.setValue(result.texte, { emitEvent: false });
      }
    }, 500);
  }

  private getCurrentValue(): string | null {
    const value = this.ngModel?.model ?? this.elementRef.nativeElement.value;
    return typeof value === 'string' ? value : null;
  }

  private isReadOnly(): boolean {
    const element = this.elementRef.nativeElement;
    return !!(element.readOnly || element.disabled);
  }

  private async showReadOnlyDisplay(): Promise<void> {
    const element = this.elementRef.nativeElement;
    const value = this.getCurrentValue();

    if (!value) {
      this.hideReadOnlyDisplay();
      return;
    }

    if (value === this.lastRenderedValue && this.displayElement) {
      return;
    }

    if (!this.displayElement) {
      this.displayElement = this.renderer.createElement('div');
      this.renderer.addClass(this.displayElement, 'formatted-text');
      this.renderer.addClass(this.displayElement, 'champConsultation');
      this.renderer.setStyle(this.displayElement, 'white-space', 'pre-wrap');
      this.renderer.insertBefore(element.parentElement, this.displayElement, element);
      this.renderer.setStyle(element, 'display', 'none');
    }

    const version = ++this.renderVersion;
    this.displayElement.innerHTML = this.safeHtmlService.sanitizeFormattedHtml(
      this.linkFormattingService.formatPlainTextLinksSync(value)
    );

    const formatted = await this.linkFormattingService.formatForDisplay(value, false);
    if (version !== this.renderVersion || !this.displayElement) {
      return;
    }

    this.displayElement.innerHTML = this.safeHtmlService.sanitizeFormattedHtml(formatted);
    this.lastRenderedValue = value;
  }

  private hideReadOnlyDisplay(): void {
    const element = this.elementRef.nativeElement;
    if (this.displayElement) {
      this.renderer.removeChild(element.parentElement, this.displayElement);
      this.displayElement = null;
    }
    this.renderer.setStyle(element, 'display', null);
    this.lastRenderedValue = null;
  }
}
